import { Box, FormControlLabel, Switch } from "@mui/material";
import React from "react";
import { COLORS } from "../../assets/theme";
import CustomTextField from "../../components/CustomTextField";
import CustomButton from "../../components/CustomButton";
import CustomSelect from "../../components/CustomSelect";
import CustomLabel from "../../components/CustomLabel";
import {
  getUnitSettings,
  updateUnitSettings,
} from "../../services/AdvancedSettings";
import { useLocation, useParams } from "react-router-dom";
import { getAllBranches } from "../../services/BranchesService";
import { getAllBusinesses } from "../../services/VenueService";
import { updateStockPoolTarget } from "../../services/StockPoolService";
import { toast } from "react-toastify";

const getErrorMessage = (error: any, fallback: string) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

const showErrorToast = (message: any) =>
  toast.error(String(message), { toastId: String(message) });

const ADVANCED_SETTINGS_TOAST_ID = "advanced-settings-toast";
const ORG_SETTINGS_TOAST_ID = "org-settings-toast";
const NO_POOL_VALUE = "";

const SLOT_DURATION_OPTIONS = [15, 30, 60];

const toTimeInputValue = (value?: string) => value || "";
const fromTimeInputValue = (value: string) => value || "09:00";

const Advanced: React.FC = () => {

  const [workTimeEnd, setWorkTimeEnd] = React.useState("17:00");
  const [workTimeStart, setWorkTimeStart] = React.useState("09:00");
  const [slotDuration, setSlotDuration] = React.useState<number>(
    SLOT_DURATION_OPTIONS[1]
  );
  const [publicMatchQuitWindowHours, setPublicMatchQuitWindowHours] =
    React.useState<number>(8);
  const [is24Hours, setIs24Hours] = React.useState(false);
  const lastCustomTimesRef = React.useRef<{ start: string; end: string }>({
    start: "09:00",
    end: "17:00",
  });

  const [selectedUnit, setSelectedUnit] = React.useState<string>("");
  const [units, setUnits] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(false);

  const [businesses, setBusinesses] = React.useState<any[]>([]);
  const [orgTargetBusinessId, setOrgTargetBusinessId] =
    React.useState<string>(NO_POOL_VALUE);
  const [currentPool, setCurrentPool] = React.useState<any>(null);
  const [orgLoading, setOrgLoading] = React.useState(false);
  const [orgSaving, setOrgSaving] = React.useState(false);

 

    const params = useParams<{ businessId?: string; id?: string }>();
  const venueId = params.businessId || params.id;
  
  if (!venueId) {
    throw new Error("Clients page requires businessId in route");
  }

  React.useEffect(() => {
    const fetchUnits = async () => {
      if (!venueId) return;

      try {
        setLoading(true);
        const response = await getAllBranches(venueId);

        const unitsData =
          response?.units || response?.data || response || [];

        setUnits(unitsData);

        if (unitsData.length > 0) {
          setSelectedUnit(unitsData[0].id);
        }
      } catch (error: any) {
        showErrorToast(getErrorMessage(error, "Failed to fetch units."));
      } finally {
        setLoading(false);
      }
    };

    fetchUnits();
  }, [venueId]);

  
  React.useEffect(() => {
    const fetchSettings = async () => {
      if (!selectedUnit) return;

      try {
        setLoading(true);
        const data = await getUnitSettings(selectedUnit);
        setWorkTimeStart(toTimeInputValue(data.open_time));
        setWorkTimeEnd(toTimeInputValue(data.close_time));
        const isFullDay =
          String(data.open_time || "").trim() === "00:00" &&
          String(data.close_time || "").trim() === "00:00";
        setIs24Hours(isFullDay);
        if (!isFullDay) {
          lastCustomTimesRef.current = {
            start: toTimeInputValue(data.open_time) || "09:00",
            end: toTimeInputValue(data.close_time) || "17:00",
          };
        }
        setSlotDuration(data.slot_duration_minutes);
        setPublicMatchQuitWindowHours(
          Number(data.public_match_quit_window_hours) || 8
        );

   
      } catch (error: any) {
        showErrorToast(getErrorMessage(error, "Failed to fetch settings."));
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [selectedUnit]);

  React.useEffect(() => {
    const fetchBusinesses = async () => {
      if (!venueId) return;

      try {
        setOrgLoading(true);
        const allBusinesses = await getAllBusinesses();
        const list = Array.isArray(allBusinesses) ? allBusinesses : [];
        setBusinesses(list);

        const current = list.find((b: any) => b.id === venueId);
        const pool = current?.stockPool || null;
        setCurrentPool(pool);

        const anchorId = pool?.anchor_business_id || pool?.anchorBusinessId;
        setOrgTargetBusinessId(
          pool && anchorId && anchorId !== venueId ? anchorId : NO_POOL_VALUE
        );
      } catch (error: any) {
        showErrorToast(getErrorMessage(error, "Failed to fetch businesses."));
      } finally {
        setOrgLoading(false);
      }
    };

    fetchBusinesses();
  }, [venueId]);

  const handleSaveOrgSettings = async () => {
    toast.dismiss(ORG_SETTINGS_TOAST_ID);
    const toastId = toast.loading("Saving org settings...", {
      toastId: ORG_SETTINGS_TOAST_ID,
      position: "top-right",
      autoClose: false,
    });

    try {
      setOrgSaving(true);
      const updated = await updateStockPoolTarget(
        venueId,
        orgTargetBusinessId || null
      );
      setCurrentPool(updated?.stockPool || null);

      toast.update(toastId, {
        render: "Org settings updated successfully!",
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });
    } catch (error: any) {
      toast.update(toastId, {
        render: getErrorMessage(error, "Failed to update org settings."),
        type: "error",
        isLoading: false,
        autoClose: 4000,
      });
    } finally {
      setOrgSaving(false);
    }
  };

  const handleSave = async () => {
    if (!selectedUnit) return;

    toast.dismiss(ADVANCED_SETTINGS_TOAST_ID);
    const toastId = toast.loading("Saving advanced settings...", {
      toastId: ADVANCED_SETTINGS_TOAST_ID,
      position: "top-right",
      autoClose: false,
    });

    try {
      setLoading(true);

      await updateUnitSettings(selectedUnit, {
        openTime: is24Hours ? "00:00" : fromTimeInputValue(workTimeStart),
        closeTime: is24Hours ? "00:00" : fromTimeInputValue(workTimeEnd),
        slotDurationMinutes: Number(slotDuration),
        publicMatchQuitWindowHours: Number(publicMatchQuitWindowHours),
      });

      toast.update(toastId, {
        render: "Advanced settings updated successfully!",
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });
    } catch {
      toast.update(toastId, {
        render: "Failed to update settings.",
        type: "error",
        isLoading: false,
        autoClose: 4000,
      });
    } finally {
      setLoading(false);
    }
  };


  return (
    <Box sx={{ px: 2, pt: 3 }}>
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Box
          sx={{
            width: "100%",
            p: 3,
            pt: 5,
            boxShadow: 2,
            borderRadius: 2,
            bgcolor: COLORS.WhiteBackground,
          }}
        >
          <CustomLabel
            text="Unit Settings"
            variant="h6"
            fontWeight={600}
            sx={{ mb: 3 }}
          />

          <Box sx={{ mb: 2 }} data-tour="opening-hours">
            <CustomLabel text="Select Unit" />
            <CustomSelect
              value={selectedUnit}
              options={units.map((unit) => ({
                label: unit.name || unit.title,
                value: unit.id,
              }))}
              onChange={(value) => setSelectedUnit(value as string)}
              fullWidth
              disabled={loading}
            />
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <CustomTextField
              label="Work Time (Start)"
              type="time"
              value={workTimeStart}
              disabled={is24Hours}
              onChange={(e) => {
                const val = e.target.value;
                setWorkTimeStart(val);
                if (!is24Hours) {
                  lastCustomTimesRef.current = {
                    ...lastCustomTimesRef.current,
                    start: val,
                  };
                }
              }}
            />

            <CustomTextField
              label="Work Time (End)"
              type="time"
              value={workTimeEnd}
              disabled={is24Hours}
              onChange={(e) => {
                const val = e.target.value;
                setWorkTimeEnd(val);
                if (!is24Hours) {
                  lastCustomTimesRef.current = {
                    ...lastCustomTimesRef.current,
                    end: val,
                  };
                }
              }}
            />
          </Box>

          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={is24Hours}
                  onChange={(e) => {
                    const next = e.target.checked;
                    setIs24Hours(next);
                    if (next) {
                      // Remember last custom times before overriding
                      lastCustomTimesRef.current = {
                        start: workTimeStart || "09:00",
                        end: workTimeEnd || "17:00",
                      };
                      setWorkTimeStart("00:00");
                      setWorkTimeEnd("00:00");
                    } else {
                      setWorkTimeStart(lastCustomTimesRef.current.start);
                      setWorkTimeEnd(lastCustomTimesRef.current.end);
                    }
                  }}
                />
              }
              label="Open 24 hours"
            />
          </Box>

          <Box sx={{ mt: 2 }}>
            <CustomLabel text="Slot Duration" />
            <CustomSelect
              value={slotDuration}
              options={SLOT_DURATION_OPTIONS.map((m) => ({
                label: `${m} minutes`,
                value: m,
              }))}
              onChange={(value) => setSlotDuration(Number(value))}
              fullWidth
            />
          </Box>

          <Box sx={{ mt: 2 }}>
            <CustomTextField
              label="Public match quit window (hours)"
              type="number"
              value={publicMatchQuitWindowHours}
              onChange={(e) =>
                setPublicMatchQuitWindowHours(
                  Math.max(1, Number(e.target.value) || 1)
                )
              }
              helperText="Players can quit a public match only within this many hours after joining."
              fullWidth
              inputProps={{ min: 1 }}
            />
          </Box>

          <Box sx={{ mt: 2 }}>
            
          </Box>

          <Box sx={{ mt: 2 }}>
            
          </Box>

          <Box sx={{ mt: 2 }}>
            
          </Box>

          <Box sx={{ mt: 2 }}>
           
          </Box>

          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <CustomButton
              label="Save"
              variant="contained"
              onClick={handleSave}
              disabled={!selectedUnit || loading}
            />
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
        <Box
          sx={{
            width: "100%",
            p: 3,
            pt: 5,
            boxShadow: 2,
            borderRadius: 2,
            bgcolor: COLORS.WhiteBackground,
          }}
        >
          <CustomLabel
            text="Org Settings"
            variant="h6"
            fontWeight={600}
            sx={{ mb: 3 }}
          />

          <Box sx={{ mb: 1 }}>
            <CustomLabel text="Share Stock With" />
            <CustomSelect
              value={orgTargetBusinessId}
              options={[
                { label: "Don't share (use own stock)", value: NO_POOL_VALUE },
                ...businesses
                  .filter((b: any) => b.id !== venueId)
                  .map((b: any) => ({
                    label: b.name || b.title,
                    value: b.id,
                  })),
              ]}
              onChange={(value) => setOrgTargetBusinessId(value as string)}
              fullWidth
              disabled={orgLoading}
            />
          </Box>

          {currentPool && Array.isArray(currentPool.members) && (
            <Box sx={{ mt: 1 }}>
              <CustomLabel
                text={`Currently sharing stock with: ${currentPool.members
                  .filter((m: any) => m.id !== venueId)
                  .map((m: any) => m.name)
                  .join(", ")}`}
                fontSize="0.85rem"
                color="#868585"
              />
            </Box>
          )}

          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <CustomButton
              label="Save"
              variant="contained"
              onClick={handleSaveOrgSettings}
              disabled={orgLoading || orgSaving}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Advanced;
